const ROOM_FLOW_RULES = [
  { rank: 10, patterns: [/living/i, /\bgreat\s*room\b/i, /\bfamily\s*room\b/i] },
  { rank: 20, patterns: [/dining/i, /\bbreakfast\b/i] },
  { rank: 30, patterns: [/kitchen/i, /\bpantry\b/i] },
  { rank: 40, patterns: [/\bprimary\b/i, /\bmaster\b/i, /\bowner'?s?\b/i] },
  { rank: 50, patterns: [/\bguest\b/i, /\bsecondary\b/i, /\bbedroom\b/i, /\bnursery\b/i] },
  { rank: 60, patterns: [/\bbath/i, /\bpowder\b/i, /\bwash\b/i] },
  { rank: 70, patterns: [/\bhome\s*office\b/i, /\boffice\b/i, /\bstudy\b/i, /\blibrary\b/i, /\bden\b/i] },
  { rank: 80, patterns: [/\blaundry\b/i, /\bmudroom\b/i, /\butility\b/i, /\bhallway\b/i, /\bfoyer\b/i, /\bentryway\b/i] },
  { rank: 90, patterns: [/\boutdoor\b/i, /\bpatio\b/i, /\bdeck\b/i, /\bporch\b/i, /\bpool\b/i, /\bterrace\b/i] },
];

function getRoomFlowRank(name) {
  const label = String(name || '').trim();
  if (!label) return 999;
  const match = ROOM_FLOW_RULES.find((rule) => rule.patterns.some((pattern) => pattern.test(label)));
  return match?.rank ?? 900;
}

export function compareRoomNamesForDesignFlow(aName, bName) {
  const aRank = getRoomFlowRank(aName);
  const bRank = getRoomFlowRank(bName);
  if (aRank !== bRank) return aRank - bRank;
  return String(aName || '').localeCompare(String(bName || ''), undefined, { sensitivity: 'base' });
}

export function sortRoomsForDesignFlow(rooms = []) {
  return [...rooms].sort((a, b) => compareRoomNamesForDesignFlow(a?.name, b?.name));
}

function normalizeMarkupNumber(value, fallback) {
  if (value === null || value === undefined || value === '') return fallback;
  const n = Number(String(value).replace('%', '').trim());
  return Number.isFinite(n) ? n : fallback;
}

export function getDefaultMarkupByItemType(item, project = {}) {
  const serviceMarkup = normalizeMarkupNumber(project?.defaultServiceMarkup, 0);
  const tradeMarkup = normalizeMarkupNumber(project?.defaultTradeMarkup, 20);
  const ffeMarkup = normalizeMarkupNumber(project?.defaultFfeMarkup, 40);

  const type = String(item?.type || '').toLowerCase();
  const category = String(item?.category || '').toLowerCase();
  const name = String(item?.name || '').toLowerCase();
  const isService = type === 'service' || category.includes('service');
  const isTradeGood =
    /fabric|trim|wall ?cover|wallpaper|textile|yard|roll|sq ft|sq\s*ft|sq yard/.test(
      `${type} ${category}`
    );

  if (isService) return serviceMarkup;
  if (isTradeGood) return tradeMarkup;
  return ffeMarkup;
}

// A catalog product with its own Selling Price starts a proposal or invoice line
// at that price: the markup is worked out from the trade price, the same way
// typing the selling price into the line does, instead of the project default.
export function getCatalogMarkup(product, fallbackMarkup) {
  const raw = product?.sellingPrice;
  const sell = Number(raw);
  if (raw == null || raw === '' || !Number.isFinite(sell) || sell <= 0) return fallbackMarkup;
  const trade = Number(product?.tradePrice ?? product?.price);
  // % markup on a $0 trade price always gives $0, so carry the price as a flat markup.
  if (!Number.isFinite(trade) || trade <= 0) return { type: 'fixed', value: sell };
  return { type: '%', value: ((sell - trade) / trade) * 100 };
}

export function toNumberOrZero(value) {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
}

export function computeLineItemDepositAmount(item) {
  const depositPct = Math.max(0, Math.min(100, toNumberOrZero(item?.deposit)));
  const lineTotal = toNumberOrZero(item?.lineTotal);
  return Number(((lineTotal * depositPct) / 100).toFixed(2));
}
